import { Link } from "react-router-dom";
import { ArrowRight, Phone, ExternalLink, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PHONE, PHONE_HREF, VISUAL_NOTE, SISTER_LINKS, PAGES } from "@/data/site";

export const PAGE_LABELS = {
  "/": "Accueil", "/franchise": "Devenir franchisé", "/contact": "Contact",
  ...Object.fromEntries(Object.entries(PAGES).map(([k, v]) => [k, v.h1])),
};

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Fil d'Ariane" className="mb-6 flex flex-wrap items-center gap-1 text-xs font-semibold text-white/70">
      {items.map((it, i) => (
        <span key={it.path} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          {i < items.length - 1 ? <Link to={it.path} className="hover:text-white">{it.name}</Link> : <span className="text-white">{it.name}</span>}
        </span>
      ))}
    </nav>
  );
}

export function PageHero({ kicker, h1, intro, image, crumbs, children }) {
  return (
    <section className="grain relative overflow-hidden bg-[#1E2422] text-white">
      <img src={image} alt={h1} className="absolute inset-0 h-full w-full object-cover opacity-35" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1E2422] via-[#1E2422]/85 to-transparent" />
      <div className="container-x relative py-16 md:py-24">
        {crumbs && <Breadcrumbs items={crumbs} />}
        <p className="fade-up mb-4 inline-block rounded-full border border-[#E9A46A]/40 bg-[#C06014]/20 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#F2C29B]">{kicker}</p>
        <h1 className="fade-up max-w-3xl text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="page-h1">{h1}</h1>
        {intro && <p className="fade-up mt-6 max-w-2xl text-base text-white/80 md:text-lg" style={{ animationDelay: ".1s" }}>{intro}</p>}
        {children}
      </div>
    </section>
  );
}

export function CtaBand({ title = "Un doute ? Inspection visuelle* et devis sous 48 h.", text = "Appelez-nous ou décrivez votre situation en ligne." }) {
  return (
    <section className="container-x my-16">
      <div className="hex-bg relative overflow-hidden rounded-2xl bg-[#C06014] p-8 text-white md:p-12">
        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-black md:text-3xl">{title}</h2>
            <p className="mt-2 text-white/85">{text}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={PHONE_HREF} className="btn-lift flex items-center gap-2 rounded-full bg-[#1E2422] px-6 py-3 font-bold" data-testid="cta-phone-button"><Phone className="h-4 w-4" /> {PHONE}</a>
            <Link to="/contact" className="btn-lift flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-[#C06014]" data-testid="cta-contact-button">Devis en ligne <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </div>
      <VisualNote className="mt-3" />
    </section>
  );
}

export const VisualNote = ({ className = "" }) => (
  <p className={`text-xs text-muted-foreground ${className}`} data-testid="visual-inspection-note">{VISUAL_NOTE}</p>
);

export function Faq({ faq }) {
  if (!faq?.length) return null;
  return (
    <section className="container-x my-16 grid gap-8 lg:grid-cols-3">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Questions fréquentes</p>
        <h2 className="mt-2 text-3xl font-black tracking-tight">Vos questions, nos réponses</h2>
      </div>
      <Accordion type="single" collapsible className="lg:col-span-2" data-testid="faq-accordion">
        {faq.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className="border-black/10">
            <AccordionTrigger className="text-left font-display text-lg font-bold" data-testid={`faq-item-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-base text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function RelatedLinks({ paths }) {
  return (
    <section className="container-x my-16">
      <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">À lire aussi</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {paths.map((p) => (
          <Link key={p} to={p} className="card-hover group flex items-center justify-between rounded-xl border bg-white p-5" data-testid={`related-link-${p.slice(1)}`}>
            <span className="font-display font-bold">{PAGE_LABELS[p] || p}</span>
            <ArrowRight className="h-4 w-4 text-[#C06014] transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export function SisterLinks() {
  return (
    <section className="container-x my-16">
      <div className="rounded-2xl border bg-white p-6 md:p-8">
        <p className="text-xs font-bold uppercase tracking-widest text-[#53634F]">Le réseau en Gironde</p>
        <h2 className="mt-2 text-2xl font-black">Nos sites spécialisés Gironde & Bordeaux</h2>
        <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {SISTER_LINKS.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener" className="group flex items-start justify-between gap-3 rounded-lg p-3 hover:bg-[#F5F3E9]" data-testid={`sister-link-${l.href.split("/").pop() || l.site}`}>
              <span><span className="block font-semibold group-hover:text-[#C06014]">{l.label}</span><span className="text-xs text-muted-foreground">{l.site}</span></span>
              <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
